<template>
  <v-container>
    <v-row>
      <v-col cols="12" style="display:flex">
        <h2 class="text-h4 mb-4">Equipment and Money</h2>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="resetDay">Reset Day</v-btn>
      </v-col>
    </v-row>
    
    <!-- Equipment Slots Section -->
    <v-row >
      <v-col cols="12">
        <v-card outlined class="pa-3 mb-4" style="background-color:#FFECC9;">
          <div class="text-h6 mb-3">Wondrous Items</div>
          <v-row>
            <v-col v-for="(slot, slotKey) in equipmentSlots" :key="slotKey" cols="3" class="mb-3">
              <v-card outlined class="pa-2" 
                      :class="{ 'equipment-slot': true, 'has-item': slot.item && slot.item.name !== 'empty' || (slotKey === 'trinkets' && slot.items && slot.items.length > 0) }"
                      @drop="handleSlotDrop($event, slotKey)"
                      @dragover.prevent
                      @dragenter.prevent>
                <div class="text-subtitle-2 mb-2">{{ getSlotDisplayName(slotKey) }}</div>
                <div v-if="slotKey === 'trinkets'" class="trinkets-container">
                  <div v-for="(item, index) in slot.items" :key="index" class="trinket-item"
                       draggable="true"
                       @dragstart="handleSlotDragStart($event, item, slotKey, index)"
                       @dragend="handleDragEnd">
                    <div class="d-flex align-center">
                      <span class="text-caption">{{ item.name }}</span>
                      <v-spacer></v-spacer>
                      <v-icon small @click="editSlotItem(slotKey, index)">edit</v-icon>
                      <v-icon small @click="removeTrinketItem(slotKey, index)">close</v-icon>
                    </div>
                    <div v-if="item.hasCharges" class="d-flex align-center">
                       <v-icon small @click.stop="decreaseCharges(slotKey, index)">remove</v-icon>
                       <span class="text-caption mx-1">{{ item.currentCharges }}/{{ item.maxCharges }}</span>
                       <v-icon small @click.stop="increaseCharges(slotKey, index)">add</v-icon>
                     </div>
                  </div>
                </div>
                <div v-else-if="slot.item && slot.item.name !== 'empty'"
                     draggable="true"
                     @dragstart="handleSlotDragStart($event, slot.item, slotKey)"
                     @dragend="handleDragEnd"
                     @click="editSlotItem(slotKey)">
                  <div class="d-flex align-center" style="justify-content: center; padding-bottom: 4px;">
                    <span style="font-weight: bold; font-size: 1.3rem;">{{ slot.item.name }}</span>
                  </div>                
                  <v-divider></v-divider>
                  <div v-if="slot.item.hasCharges" class="d-flex align-center mt-1" style="justify-content: right;">
                     <v-icon small @click.stop="decreaseCharges(slotKey)">remove</v-icon>
                     <span class="text-caption mx-1">{{ slot.item.currentCharges }}/{{ slot.item.maxCharges }}</span>
                     <v-icon small @click.stop="increaseCharges(slotKey)">add</v-icon>
                   </div>
                </div>
                <div v-else class="empty-slot text-caption text-center">
                  Empty
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Equipment Sections -->
    <v-row>
      <v-col cols="12">
        <v-card outlined class="pa-3 mb-4" style="background-color:#FFECC9;">
          <div class="d-flex align-center mb-3">
            <div class="text-h6">Bags</div>
            <v-spacer></v-spacer>
            <v-btn small @click="addEquipmentSection">Add Section</v-btn>
          </div>
          
          <v-row>
            <v-col v-for="(section, sectionIndex) in characterData.equipment.backpack" :key="sectionIndex" cols="6">
              <v-card outlined class="pa-3 mb-3">
                <div class="d-flex align-center mb-2">
                  <v-text-field
                    v-model="section.name"
                    label="Section Name"
                    hide-details
                    dense
                    class="mr-2"
                  ></v-text-field>
                  <v-btn small icon @click="removeEquipmentSection(sectionIndex)" color="error">
                    <v-icon>delete</v-icon>
                  </v-btn>
                </div>
                
                  <div class="equipment-list"
                     @drop="handleEquipmentDrop($event, sectionIndex)"
                     @dragover.prevent
                     @dragenter.prevent
                     @dragleave.prevent>
                  <draggable 
                    :list="section.items" 
                    group="equipment"
                    handle=".drag-handle"
                    @end="handleDragEnd">
                    <div v-for="(item, itemIndex) in section.items" :key="itemIndex" 
                         class="equipment-item mb-2 pa-2" 
                         draggable="true"
                         @dragstart="handleDragStart($event, item, sectionIndex, itemIndex)"
                         @dragend="handleDragEnd">
                      <div class="d-flex align-center" @click="editItem(sectionIndex, itemIndex)">
                          <v-icon class="drag-handle mr-2" style="cursor: grab;">mdi-drag</v-icon>
                          <span class="text-caption">{{ item.name }}</span>
                           <div v-if="item.hasCharges" class="d-flex align-right mt-1" style="justify-content: right; margin-left: auto;">
                             <v-icon small @click.stop="decreaseEquipmentCharges(sectionIndex, itemIndex)">remove</v-icon>
                             <span class="text-caption mx-1">{{ item.currentCharges }}/{{ item.maxCharges }}</span>
                             <v-icon small @click.stop="increaseEquipmentCharges(sectionIndex, itemIndex)">add</v-icon>
                           </div>
                      </div>
                    </div>
                  </draggable>
                </div>
                
                <div class="text-center mt-2">
                  <v-btn small @click="addNewItem(sectionIndex)">Add Item</v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Money Section -->
    <v-row>
      <v-col cols="12">
        <v-card outlined class="pa-3 mb-4" style="background-color:#FFECC9;">
          <div class="text-h6 mb-3">Money</div>
          <v-row>
            <v-col cols="3">
              <v-text-field
                v-model="characterData.equipment.money.cp"
                label="Copper"
                type="number"
                hide-details
                class="text-center"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="characterData.equipment.money.sp"
                label="Silver"
                type="number"
                hide-details
                class="text-center"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="characterData.equipment.money.gp"
                label="Gold"
                type="number"
                hide-details
                class="text-center"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="characterData.equipment.money.pp"
                label="Platinum"
                type="number"
                hide-details
                class="text-center"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Equipment Item Modal -->
    <v-dialog v-model="showItemDialog" max-width="500px">
      <v-card>
        <v-card-title>
          {{ editingItem ? 'Edit Item' : 'New Item' }}
          <v-spacer></v-spacer>
          <v-icon medium color="error" @click.stop="removeItem(editingSectionIndex, editingItemIndex, editingSlotKey); showItemDialog = false;">delete</v-icon>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newItem.name"
            label="Item Name"
            required
            hide-details
            class="mb-3"
          ></v-text-field>

          <v-text-field
            v-model="newItem.value"
            label="Value"
            step="0.1"
            hide-details
            class="mb-3"
          ></v-text-field>
          
          <v-text-field
            v-model="newItem.weight"
            label="Weight"
            type="number"
            step="0.1"
            hide-details
            class="mb-3"
          ></v-text-field>
          
          <v-textarea
            v-model="newItem.description"
            label="Description"
            rows="4"
            auto-grow
            hide-details
            class="mb-3"
          ></v-textarea>
          
          <v-checkbox
            v-model="newItem.hasCharges"
            label="Has Charges"
            hide-details
            class="mb-3"
          ></v-checkbox>
          
          <v-row v-if="newItem.hasCharges">
            <v-col cols="6">
              <v-text-field
                v-model="newItem.currentCharges"
                label="Current Charges"
                type="number"
                min="0"
                :max="newItem.maxCharges"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="newItem.maxCharges"
                label="Max Charges"
                type="number"
                min="1"
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row v-if="newItem.hasCharges">
            <v-col cols="4">
              <v-checkbox
              v-if="newItem.hasCharges"
              v-model="newItem.recharges"
              label="Recharges"
              hide-details
              class="mb-3"
            ></v-checkbox>
            </v-col>
            <v-col cols="8">
            <v-text-field
              v-if="newItem.recharges"
              v-model="newItem.dailyRecharges"
              label="Daily Recharge (leave blank if all)"
              type="number"
              min="0"
              hide-details
              class="mb-3"
            ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="showItemDialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" @click="saveItem">
            {{ editingItem ? 'Update' : 'Add' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import draggable from 'vuedraggable';
export default {
  components: {
    draggable,
  },
  props: {
    character: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showItemDialog: false,
      editingItem: false,
      editingSectionIndex: -1,
      editingItemIndex: -1,
      editingSlotKey: null,
      editingSlotIndex: -1,
      newItem: {
        name: '',
        value: 0,
        weight: 0,
        description: '',
        hasCharges: false,
        maxCharges: 1,
        currentCharges: 1,
        recharges: false,
        dailyRecharges: 0
      },
      draggedItem: null,
      draggedSectionIndex: -1,
      draggedItemIndex: -1,
      draggedSlotKey: null,
      draggedSlotIndex: -1,
      draggedFromSlot: false
    }
  },
  computed: {
    characterData() {
      return this.character.characterData;
    },
    equipmentSlots() {
      return this.characterData.equipment.equipmentSlots;
    },
  },
  mounted() {
    if (!this.characterData.equipment.backpack || this.characterData.equipment.backpack.length === 0) {
      this.characterData.equipment.backpack = [{ name: 'Main Bag', items: [] }];
    }
  },
  methods: {
    getSlotDisplayName(slotKey) {
      const names = {
        ring1: 'Ring 1',
        ring2: 'Ring 2',
        neck: 'Neck',
        eyes: 'Eyes',
        belt: 'Belt',
        hands: 'Hands',
        wrist: 'Wrist',
        shoulders: 'Shoulders',
        chest: 'Chest',
        headband: 'Headband',
        body: 'Body',
        feet: 'Feet',
        head: 'Head',
        trinkets: 'Trinkets'
      };
      return names[slotKey] || slotKey.charAt(0).toUpperCase() + slotKey.slice(1);
    },

    addEquipmentSection() {
      this.characterData.equipment.backpack.push({
        name: 'New Section',
        items: []
      });
    },

    removeEquipmentSection(sectionIndex) {
      if (this.characterData.equipment.backpack.length > 1) {
        this.characterData.equipment.backpack.splice(sectionIndex, 1);
      }
    },

    addNewItem(sectionIndex) {
      this.editingItem = false;
      this.editingSectionIndex = sectionIndex;
      this.editingItemIndex = -1;
      this.resetNewItem();
      this.showItemDialog = true;
    },

    editItem(sectionIndex, itemIndex) {
      this.editingItem = true;
      this.editingSectionIndex = sectionIndex;
      this.editingItemIndex = itemIndex;
      const item = this.characterData.equipment.backpack[sectionIndex].items[itemIndex];
      this.newItem = { ...item };
      this.showItemDialog = true;
    },

    editSlotItem(slotKey, index = null) {
      this.editingItem = true;
      this.editingSlotKey = slotKey;
      this.editingSlotIndex = index;
      
      let item;
      if (slotKey === 'trinkets') {
        item = this.characterData.equipment.equipmentSlots[slotKey].items[index];
      } else {
        item = this.characterData.equipment.equipmentSlots[slotKey].item;
      }
      
      this.newItem = { ...item };
      this.showItemDialog = true;
    },

    saveItem() {
      if (!this.newItem.name.trim()) return;

      if (this.editingItem) {
        if (this.editingSlotKey) {
          // Update item in slot
          if (this.editingSlotKey === 'trinkets') {
            this.characterData.equipment.equipmentSlots[this.editingSlotKey].items[this.editingSlotIndex] = { ...this.newItem };
          } else {
            this.characterData.equipment.equipmentSlots[this.editingSlotKey].item = { ...this.newItem };
          }
          this.editingSlotKey = null;
          this.editingSlotIndex = -1;
        } else {
          // Update existing item in backpack
          this.characterData.equipment.backpack[this.editingSectionIndex].items[this.editingItemIndex] = { ...this.newItem };
        }
      } else {
        // Add new item to backpack
        this.characterData.equipment.backpack[this.editingSectionIndex].items.push({ ...this.newItem });
      }
      this.showItemDialog = false;
      this.resetNewItem();
    },

    resetNewItem() {
      this.newItem = {
        name: '',
        value: 0,
        weight: 0,
        description: '',
        hasCharges: false,
        maxCharges: 1,
        currentCharges: 1,
        recharges: false,
        dailyRecharges: 0
      };
      this.editingSlotKey = null;
      this.editingSlotIndex = -1;
    },

    removeItem(sectionIndex, itemIndex, slotKey) {
      if(slotKey) {
        this.characterData.equipment.equipmentSlots[slotKey].item = null;
      } else {
        this.characterData.equipment.backpack[sectionIndex].items.splice(itemIndex, 1);
      }
    },

    handleDragStart(event, item, sectionIndex, itemIndex) {
      this.draggedItem = { ...item };
      this.draggedSectionIndex = sectionIndex;
      this.draggedItemIndex = itemIndex;
      this.draggedFromSlot = false;
      event.dataTransfer.effectAllowed = 'move';
    },

    handleSlotDragStart(event, item, slotKey, index = null) {
      this.draggedItem = { ...item };
      this.draggedSlotKey = slotKey;
      this.draggedSlotIndex = index;
      this.draggedFromSlot = true;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', JSON.stringify({ slotKey, index }));
    },

    handleDragEnd() {
      this.draggedItem = null;
      this.draggedSectionIndex = -1;
      this.draggedItemIndex = -1;
      this.draggedSlotKey = null;
      this.draggedSlotIndex = -1;
      this.draggedFromSlot = false;
    },

    handleEquipmentDrop(event, sectionIndex) {
      if (!this.draggedItem) return;

      if (this.draggedFromSlot) {
        // Dragging from slot to backpack
        this.characterData.equipment.backpack[sectionIndex].items.push(this.draggedItem);

        if (this.draggedSlotKey === 'trinkets') {
          this.characterData.equipment.equipmentSlots.trinkets.items.splice(this.draggedSlotIndex, 1);
        } else {
          this.characterData.equipment.equipmentSlots[this.draggedSlotKey].item = null;
        }
      } else if (this.draggedSectionIndex !== sectionIndex) {
        // Dragging between backpack sections
        this.characterData.equipment.backpack[this.draggedSectionIndex].items.splice(this.draggedItemIndex, 1);
        this.characterData.equipment.backpack[sectionIndex].items.push(this.draggedItem);
      }

      this.handleDragEnd();
    },

    handleSlotDrop(event, slotKey) {
      if (!this.draggedItem) return;

      if (this.draggedFromSlot) {
        // Dragging from one slot to another
        if (slotKey === 'trinkets') {
          // Moving to trinkets
          this.characterData.equipment.equipmentSlots.trinkets.items.push(this.draggedItem);

          // Remove from original slot
          if (this.draggedSlotKey === 'trinkets') {
            this.characterData.equipment.equipmentSlots.trinkets.items.splice(this.draggedSlotIndex, 1);
          } else {
            this.characterData.equipment.equipmentSlots[this.draggedSlotKey].item = null;
          }
        } else if (this.draggedSlotKey === 'trinkets') {
          // Moving from trinkets to regular slot
          const currentItem = this.characterData.equipment.equipmentSlots[slotKey].item;
          if (currentItem && currentItem.name !== 'empty') {
            this.characterData.equipment.equipmentSlots.trinkets.items.push(currentItem);
          }
          this.characterData.equipment.equipmentSlots[slotKey].item = this.draggedItem;
          this.characterData.equipment.equipmentSlots.trinkets.items.splice(this.draggedSlotIndex, 1);
        } else {
          // Moving between regular slots
          const currentItem = this.characterData.equipment.equipmentSlots[slotKey].item;
          this.characterData.equipment.equipmentSlots[slotKey].item = this.draggedItem;
          this.characterData.equipment.equipmentSlots[this.draggedSlotKey].item = currentItem;
        }
      } else {
        // Dragging from backpack to slot
        if (slotKey === 'trinkets') {
          this.characterData.equipment.equipmentSlots.trinkets.items.push(this.draggedItem);
          this.characterData.equipment.backpack[this.draggedSectionIndex].items.splice(this.draggedItemIndex, 1);
        } else {
          const currentItem = this.characterData.equipment.equipmentSlots[slotKey].item;
          this.characterData.equipment.equipmentSlots[slotKey].item = this.draggedItem;
          const sourceItems = this.characterData.equipment.backpack[this.draggedSectionIndex].items;
          if (currentItem && currentItem.name !== 'empty') {
            sourceItems.splice(this.draggedItemIndex, 1, currentItem);
          } else {
            sourceItems.splice(this.draggedItemIndex, 1);
          }
        }
      }

      this.handleDragEnd();
    },

    removeSlotItem(slotKey) {
      if (slotKey === 'trinkets') return;
      this.characterData.equipment.equipmentSlots[slotKey].item = null;
    },

    destroySlotItem(slotKey) {
      if (slotKey === 'trinkets') return;
      this.characterData.equipment.equipmentSlots[slotKey].item = null;
    },

    removeTrinketItem(slotKey, index) {
      this.characterData.equipment.equipmentSlots[slotKey].items.splice(index, 1);
    },

    increaseCharges(slotKey, index = null) {
      let item;
      if (slotKey === 'trinkets') {
        item = this.characterData.equipment.equipmentSlots[slotKey].items[index];
      } else {
        item = this.characterData.equipment.equipmentSlots[slotKey].item;
      }
      if (item && item.currentCharges < item.maxCharges) {
        item.currentCharges++;
      }
      this.$forceUpdate();
    },

    decreaseCharges(slotKey, index = null) {
      let item;
      if (slotKey === 'trinkets') {
        item = this.characterData.equipment.equipmentSlots[slotKey].items[index];
      } else {
        item = this.characterData.equipment.equipmentSlots[slotKey].item;
      }
      if (item && item.currentCharges > 0) {
        item.currentCharges--;
      }
      this.$forceUpdate();
    },

    increaseEquipmentCharges(sectionIndex, itemIndex) {
      const item = this.characterData.equipment.backpack[sectionIndex].items[itemIndex];
      if (item.currentCharges < item.maxCharges) {
        item.currentCharges++;
      }
      this.$forceUpdate();
    },

    decreaseEquipmentCharges(sectionIndex, itemIndex) {
      const item = this.characterData.equipment.backpack[sectionIndex].items[itemIndex];
      if (item.currentCharges > 0) {
        item.currentCharges--;
      }
      this.$forceUpdate();
    },

    resetDay() {
      let slots = ['head', 'headband', 'eyes', 'neck', 'shoulders', 'chest', 'body', 'belt', 'wrist', 'hands', 'ring1', 'ring2', 'feet'];
      for(let i = 0; i < slots.length; i++) {
        this.resetItemCharges(this.characterData.equipment.equipmentSlots[slots[i]].item);
      }
      for(let i = 0; i < this.characterData.equipment.equipmentSlots.trinkets.items.length; i++) {
        this.resetItemCharges(this.characterData.equipment.equipmentSlots.trinkets.items[i]);
      }
      for(let i = 0; i < this.characterData.equipment.backpack.length; i++) {
        for(let j = 0; j < this.characterData.equipment.backpack[i].items.length; j++) {
          this.resetItemCharges(this.characterData.equipment.backpack[i].items[j]);
        }
      }
      // Force reactivity update for trinkets
      this.$forceUpdate();
    },
    resetItemCharges(item) {
        if(item.recharges) {
          if(item.dailyRecharges > 0) {
            const newCharges = parseInt(item.currentCharges) + parseInt(item.dailyRecharges);
            const finalCharges = newCharges > item.maxCharges ? item.maxCharges : newCharges;
            this.$set(item, 'currentCharges', finalCharges);
          } else {
            this.$set(item, 'currentCharges', item.maxCharges);
          }
        }
    }
  }
}
</script>

<style scoped>
.equipment-slot {
  min-height: 80px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.equipment-slot:hover {
  background-color: #f5f5f5;
}

.equipment-slot.has-item {
  background-color: #7d542b;
  color: antiquewhite;
}

.empty-slot {
  color: #999;
  font-style: italic;
}

.equipment-item {
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.equipment-item:hover {
  background-color: #e3f2fd;
  border-color: #2196f3;
}

.trinkets-container {
  max-height: 120px;
  overflow-y: auto;
}

.trinket-item {
  background-color: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 4px;
  padding: 4px;
  margin-bottom: 4px;
  font-size: 0.75rem;
  color:saddlebrown;
}

.equipment-list {
  min-height: 100px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
}
</style>